import { useState, useEffect } from "react";
import { Text, mergeStyles, Icon, Dialog, DialogType, DialogFooter, PrimaryButton, DefaultButton, Spinner, SpinnerSize } from "@fluentui/react";
import { IAgreementObjectId, AgreementObjectType } from "models/agreements";
import NoItemSelectedDisplay from "components/shared/NoItemSelectedDisplay";
import useAgreementObjectDialog from "hooks/useAgreementObjectDialog";
import { useGetAllPoliciesQuery, useWithdrawPolicyMutation } from "services/policy";
import { useGetPolicyExecutionStatusQuery } from "services/implicitAcceptance";
import useRouter from "hooks/useRouter";
import { routeDefinitions } from "router";
import { environmentConfig } from "config/environmentConfig";
import WithLoading from "components/shared/WithLoading";
import { PolicyInfoShimmer } from "components/loadingSkeletons/PolicyLoadingSkeletons";
import { hasReadOnlyAccess } from "components/AppContent";

// Separate component for the policy content that will be wrapped with WithLoading
interface PolicyInfoContentProps {
  policy: any;
  executionStatus: any;
  goToRoute: any;
  withdrawPolicy: any;
  openDeletionDialog: any;
  isWithdrawDialogOpen: boolean;
  setIsWithdrawDialogOpen: (value: boolean) => void;
  isWithdrawing: boolean;
  setIsWithdrawing: (value: boolean) => void;
}

// eslint-disable-next-line react/function-component-definition
const PolicyInfoContent = ({
  policy,
  executionStatus,
  goToRoute,
  withdrawPolicy,
  openDeletionDialog,
  isWithdrawDialogOpen,
  setIsWithdrawDialogOpen,
  isWithdrawing,
  setIsWithdrawing,
}: PolicyInfoContentProps) => {
  const metadataRowStyle = mergeStyles({
    display: 'flex',
    alignItems: 'center',
    padding: '8px 0',
    gap: '12px'
  });

  const labelStyle = mergeStyles({
    color: '#323130',
    fontWeight: 600,
    fontSize: '14px',
    fontFamily: 'Segoe UI',
    minWidth: '190px'
  });

  const valueStyle = mergeStyles({
    color: '#323130',
    fontWeight: 400,
    fontSize: '14px',
    fontFamily: 'Segoe UI'
  });

  const actionButtonStyle = mergeStyles({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    fontSize: '14px',
    fontWeight: 400,
    fontFamily: 'Segoe UI',
    backgroundColor: 'transparent',
    color: '#323130',
    lineHeight: '20px',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '2px',
    ':hover': {
      backgroundColor: '#F3F2F1',
    },
  });

  const disabledButtonStyle = mergeStyles({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    fontSize: '14px',
    fontWeight: 400,
    fontFamily: 'Segoe UI',
    backgroundColor: 'transparent',
    color: '#A19F9D',
    lineHeight: '20px',
    border: 'none',
    cursor: 'not-allowed',
    borderRadius: '2px',
    opacity: 0.6,
  });

  // Only use API data if available AND it matches the current policy ID
  const currentPolicyId = policy?.objectIdInfo?.policyId;
  const isDataForCurrentPolicy = executionStatus?.customStatus?.PolicyId === currentPolicyId;
  const hasExecutionData = isDataForCurrentPolicy && executionStatus?.customStatus?.TotalAgreements !== undefined;
  const processed = hasExecutionData ? (executionStatus?.customStatus?.ProcessedAgreements ?? 0) : 0;
  const total = hasExecutionData ? (executionStatus?.customStatus?.TotalAgreements ?? 0) : 0;
  const progress = total > 0 ? (processed / total) * 100 : 0;

  // Check if policy effective date (agreement effective date) is in the past
  // Policies can only be deleted/withdrawn BEFORE the policy effective date
  const isPolicyEffectiveDateInPast = () => {
    // Get the agreement effective date from policy data
    const effectiveDateStr = (policy as any).agreementEffectiveDate;
    if (!effectiveDateStr) {
      return false; // No effective date means not yet set, can delete/withdraw
    }
    try {
      const effectiveDate = new Date(effectiveDateStr);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to compare dates only
      effectiveDate.setHours(0, 0, 0, 0);
      
      return effectiveDate <= today;
    } catch {
      return false;
    }
  };

  // Delete and Withdraw buttons should be enabled only for:
  // 1. Policies with "Published" or "Live" status
  // 2. Before the policy effective date (agreement effective date)
  const isPublishedStatus = policy.status === 'Published' || policy.status === 'Live';
  const isActionDisabled = isPolicyEffectiveDateInPast();
  const isWithdrawDisabled = !isPublishedStatus || isActionDisabled || hasReadOnlyAccess();
  const isDeleteDisabled = policy.status !== 'Draft' || hasReadOnlyAccess();

  // Handle delete button click
  const handleDeleteClick = () => {
    openDeletionDialog({
      title: "Delete policy?",
      objectType: AgreementObjectType.policy,
      objectId: policy.objectIdInfo?.policyId || '',
      objectEtag: policy.etag,
      policyStatus: policy.status,
      policyType: policy.type,
      cloud: environmentConfig.cloud,
      environment: environmentConfig.environment,
    });
  };

  // Handle withdraw button click
  const handleWithdrawClick = () => {
    setIsWithdrawDialogOpen(true);
  };

  // Handle withdraw confirmation
  const handleWithdrawConfirm = async () => {
    setIsWithdrawing(true);
    try {
      await withdrawPolicy({
        id: policy.objectIdInfo.policyId || '',
        status: policy.status,
        cloud: environmentConfig.cloud,
        environment: environmentConfig.environment,
        policyType: policy.type,
      }).unwrap();
      setIsWithdrawDialogOpen(false);
      // Policy will return to Draft status after withdrawal
    } catch (err) {
      // Failed to withdraw policy
    } finally {
      setIsWithdrawing(false);
    }
  };

  // Handle withdraw dialog dismiss
  const handleWithdrawDismiss = () => {
    if (!isWithdrawing) {
      setIsWithdrawDialogOpen(false);
    }
  };

  // Compute status badge styling
  const isTestPublished = policy.status === 'Published' && policy.isTestPolicy;
  const isDraft = policy.status === 'Draft';
  // eslint-disable-next-line no-nested-ternary
  const badgeBgColor = isDraft ? '#FFF4CE' : isTestPublished ? '#EFF6FC' : '#DFF6DD';
  // eslint-disable-next-line no-nested-ternary
  const badgeTextColor = isDraft ? '#605E5C' : isTestPublished ? '#005A9E' : '#107C10';
  // eslint-disable-next-line no-nested-ternary
  const chevronColor = isDraft ? '#605E5C' : isTestPublished ? '#005A9E' : '#107C10';
  const statusLabel = isTestPublished ? 'Published (Test)' : policy.status;

  return (
    <div style={{ padding: 0, width: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Text style={{
          fontWeight: 600,
          color: '#323130',
          fontSize: '22px',
          fontFamily: 'Segoe UI',
          lineHeight: '30px',
          display: 'block',
          marginBottom: '16px'
        }}>
          {policy.name}
        </Text>

        {/* Status badges and actions */}
        <div style={{ display: 'flex', gap: '0px', alignItems: 'center' }}>
          <button type="button" style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            fontSize: '14px',
            fontWeight: 400,
            fontFamily: 'Segoe UI',
            backgroundColor: badgeBgColor,
            color: badgeTextColor,
            lineHeight: '20px',
            borderRadius: '2px',
            border: 'none',
            cursor: 'pointer'
          }}>
            {(policy.status === 'Live' || policy.status === 'Published') && <Icon iconName="Clock" style={{ fontSize: '16px', color: isTestPublished ? '#005A9E' : '#107C10' }} />}
            {isDraft && <Icon iconName="PageEdit" style={{ fontSize: '16px', color: '#605E5C' }} />}
            {statusLabel}
            <Icon iconName="ChevronDown" style={{ fontSize: '12px', color: chevronColor }} />
          </button>
          <button
            type="button"
            className={actionButtonStyle}
            onClick={() => {
              goToRoute(
                routeDefinitions.PolicyEdit.getRouteInfo({
                  policyId: policy.objectIdInfo.policyId,
                })
              );
            }}>
            <Icon iconName="TextDocument" style={{ fontSize: '16px', color: '#0078D4' }} />
            Open policy
          </button>

          <button
            type="button"
            disabled={isDeleteDisabled}
            className={isDeleteDisabled ? disabledButtonStyle : actionButtonStyle}
            onClick={handleDeleteClick}>
            <Icon iconName="Delete" style={{ fontSize: '16px', color: isDeleteDisabled ? '#A19F9D' : '#605E5C' }} />
            Delete 
          </button>

          {(policy.status === 'Live' || policy.status === 'Published') && <>
            <div style={{
              width: '1px',
              height: '24px',
              backgroundColor: '#D1D1D1',
              margin: '0 4px'
            }} />
            <button
              type="button"
              disabled={isWithdrawDisabled || isWithdrawing}
              className={(isWithdrawDisabled || isWithdrawing) ? disabledButtonStyle : actionButtonStyle}
              onClick={handleWithdrawClick}>
              {isWithdrawing ? (
                <Spinner size={SpinnerSize.xSmall} style={{ marginRight: 2 }} />
              ) : (
                <Icon iconName="Undo" style={{ fontSize: '16px', color: isWithdrawDisabled ? '#A19F9D' : '#605E5C' }} />
              )}
              {isWithdrawing ? 'Withdrawing...' : 'Withdraw'}
            </button></>}
        </div>
      </div>

      {/* Warning banner when policy effective date is in the past */}
      {/* {isButtonsDisabled && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#FFF4CE',
          border: '1px solid #F7E6B5',
          marginBottom: '24px',
          fontFamily: 'Segoe UI',
          fontSize: '14px',
          lineHeight: '20px',
          color: '#323130'
        }}>
          If policy effective date is in the past, disable delete and withdraw buttons.
        </div>
      )} */}

      {/* Metadata section */}
      <div style={{
        backgroundColor: '#FFFFFF',
        padding: '16px 20px',
        marginBottom: '24px',
        border: '1px solid #EDEBE9',
        borderRadius: '2px',
        minWidth: '420px',
        width: 'fit-content',
        boxSizing: 'border-box'
      }}>
        <div className={metadataRowStyle}>
          <Text className={labelStyle}>Content refresh date:</Text>
          <Text className={valueStyle}>
            {policy.contentRefreshDate ? new Date(policy.contentRefreshDate as string).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}
          </Text>
        </div>
        <div className={metadataRowStyle}>
          <Text className={labelStyle}>Agreement effective date:</Text>
          <Text className={valueStyle}>
            {policy.agreementEffectiveDate ? new Date(policy.agreementEffectiveDate as string).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}
          </Text>
        </div>
        <div className={metadataRowStyle}>
          <Text className={labelStyle}>Last Modified:</Text>
          {policy.modifiedDate ? new Date(policy.modifiedDate as string).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}
        </div>
        <div className={metadataRowStyle}>
          <Text className={labelStyle}>Modified by:</Text>
          <Text className={valueStyle}>{(policy.modifiedBy as string) || 'N/A'}</Text>
        </div>
      </div>

      {/* Policy description */}
      <div style={{ marginBottom: '24px' }}>
        <Text style={{
          fontWeight: 600,
          color: '#323130',
          fontSize: '14px',
          fontFamily: 'Segoe UI',
          display: 'block',
          marginBottom: '8px'
        }}>
          Policy description
        </Text>
        <Text style={{
          lineHeight: '20px',
          color: '#323130',
          fontSize: '14px',
          fontFamily: 'Segoe UI',
          display: 'block',
          wordBreak: 'break-word',
          overflowWrap: 'break-word'
        }}>
          {policy.description}
        </Text>
      </div>
      {policy.status !== 'Draft' && (
        <>
          {/* Policy execution */}
          <div style={{ marginBottom: '24px' }}>
            <Text style={{
              fontWeight: 600,
              color: '#323130',
              fontSize: '14px',
              fontFamily: 'Segoe UI',
              display: 'block',
              marginBottom: '8px'
            }}>
              Policy execution :
            </Text>
            <Text style={{
              color: '#323130',
              fontSize: '14px',
              fontFamily: 'Segoe UI',
              display: 'block'
            }}>
              Estimated time for all agreements to be generated ~2-3 days
            </Text>
          </div>

          {/* Progress section - only show if API data is available */}
          {hasExecutionData && (
            <div>
              <Text style={{
                fontWeight: 600,
                color: '#323130',
                fontSize: '14px',
                fontFamily: 'Segoe UI',
                display: 'block',
                marginBottom: '8px'
              }}>
                Progress : {Math.round(progress)}% completed
              </Text>
              <div style={{
                width: '100%',
                height: '4px',
                backgroundColor: '#EDEBE9',
                marginBottom: '8px',
                position: 'relative'
              }}>
                <div style={{
                  width: `${progress}%`,
                  height: '100%',
                  backgroundColor: '#0078D4',
                  position: 'absolute',
                  left: 0,
                  top: 0
                }} />
              </div>
              <Text style={{
                color: '#605E5C',
                fontSize: '12px',
                fontFamily: 'Segoe UI'
              }}>
                {processed}/{total} agreements generated
              </Text>
            </div>
          )}
        </>
      )}

      {/* Withdraw Confirmation Dialog */}
      <Dialog
        hidden={!isWithdrawDialogOpen}
        onDismiss={handleWithdrawDismiss}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Withdraw Policy?',
          subText: `Are you sure you want to withdraw the policy "${policy.name}"? Once withdrawn, the policy will return to Draft status and can be edited and republished.`,
        }}
        modalProps={{
          isBlocking: isWithdrawing,
          styles: { main: { maxWidth: 450 } },
        }}
      >
        <DialogFooter>
          <PrimaryButton
            onClick={handleWithdrawConfirm}
            disabled={isWithdrawing}
          >
            {isWithdrawing ? (
              <>
                <Spinner size={SpinnerSize.xSmall} styles={{ root: { marginRight: 8 } }} />
                Withdrawing
              </>
            ) : (
              'Withdraw'
            )}
          </PrimaryButton>
          <DefaultButton
            onClick={handleWithdrawDismiss}
            text="Cancel"
            disabled={isWithdrawing}
          />
        </DialogFooter>
      </Dialog>
    </div>
  );
};

// Create the WithLoading wrapped component
const PolicyInfoContentWithLoading = WithLoading(PolicyInfoContent);

// eslint-disable-next-line react/function-component-definition
const PolicyInfo = ({ policyId, isNothingSelected }: IAgreementObjectId) => {
  const { data: policies, isLoading, error } = useGetAllPoliciesQuery({ 
    cloud: environmentConfig.cloud, 
    environment: environmentConfig.environment 
  });
  const { goToRoute } = useRouter();
  const [withdrawPolicy] = useWithdrawPolicyMutation();
  const { openDeletionDialog } = useAgreementObjectDialog();
  const [isWithdrawDialogOpen, setIsWithdrawDialogOpen] = useState(false);
  const [isWithdrawing, setIsWithdrawing] = useState(false);

  // Find the policy from the list based on policyId
  const policy = policies?.find(p => p.objectIdInfo.policyId === policyId);

  // Fetch policy execution status from Implicit Acceptance API (no polling - manual refetch only)
  const { data: executionStatus, refetch: refetchExecutionStatus } = useGetPolicyExecutionStatusQuery(policy?.objectIdInfo?.policyId || '', {
    skip: !policy?.objectIdInfo?.policyId || policy?.status === 'Draft'
  });

  // Refetch execution status when policy is clicked/changed
  useEffect(() => {
    if (policyId && policy && policy.status !== 'Draft') {
      // Trigger refetch for the new policy
      refetchExecutionStatus();
    }
  }, [policyId, policy, refetchExecutionStatus]);

  if (isNothingSelected) {
    return <NoItemSelectedDisplay itemType="policy" />;
  }

  // Show shimmer when loading or when policy is not found
  const showLoading = isLoading || !!error || !policy;

  return (
    <PolicyInfoContentWithLoading
      isLoading={showLoading}
      LoadingSubstitute={PolicyInfoShimmer}
      policy={policy}
      executionStatus={executionStatus}
      goToRoute={goToRoute}
      withdrawPolicy={withdrawPolicy}
      openDeletionDialog={openDeletionDialog}
      isWithdrawDialogOpen={isWithdrawDialogOpen}
      setIsWithdrawDialogOpen={setIsWithdrawDialogOpen}
      isWithdrawing={isWithdrawing}
      setIsWithdrawing={setIsWithdrawing}
    />
  );
};

export default PolicyInfo;